import React from "react";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function shiftPeriod(period, delta) {
  const [y, m] = period.split("-").map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** Page-level period selector (Employee Profile "Monthly Record" - Phase 2). Drives the
 * shared `?period=YYYY-MM` URL state consumed by the Attendance tab and every Monthly
 * Record section - there is only ONE month/year control on this page. */
export const PeriodSelector = ({ period, onChange }) => {
  const [year, month] = period.split("-");
  const years = Array.from({ length: 8 }, (_, i) => String(new Date().getFullYear() - 5 + i));
  const isCurrentMonth = period === currentPeriod();

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="monthly-period-selector">
      <CalendarClock size={16} className="text-slate-500" />
      <button
        data-testid="period-prev-month-button"
        onClick={() => onChange(shiftPeriod(period, -1))}
        className="p-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
        aria-label="Previous month"
      >
        <ChevronLeft size={14} />
      </button>
      <Select value={month} onValueChange={(v) => onChange(`${year}-${v}`)}>
        <SelectTrigger data-testid="period-month-select" className="w-24 h-8"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={year} onValueChange={(v) => onChange(`${v}-${month}`)}>
        <SelectTrigger data-testid="period-year-select" className="w-24 h-8"><SelectValue /></SelectTrigger>
        <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
      </Select>
      <button
        data-testid="period-next-month-button"
        onClick={() => onChange(shiftPeriod(period, 1))}
        className="p-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
        aria-label="Next month"
      >
        <ChevronRight size={14} />
      </button>
      {!isCurrentMonth && (
        <button
          data-testid="period-current-month-button"
          onClick={() => onChange(currentPeriod())}
          className="px-3 py-1.5 rounded-full border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50 active:scale-95 transition-all"
        >
          This month
        </button>
      )}
    </div>
  );
};

export { currentPeriod };
